import { Sparkles, KeyRound } from "lucide-react";
import useSettings from "./useSettings";
import { SettingsHeader, Section, Loading } from "./_shared";

// Curated model list — kept small to avoid overwhelming the operator.
// Any provider/model supported by emergentintegrations can be entered manually
// via the two "custom" fields at the bottom.
const PRESETS = [
  { label: "Claude Sonnet 4.6 (recomendado — mejor calidad)", provider: "anthropic", model: "claude-sonnet-4-6" },
  { label: "Claude Sonnet 4.5", provider: "anthropic", model: "claude-sonnet-4-5-20250929" },
  { label: "Claude Haiku 4.5 (más rápido y barato)", provider: "anthropic", model: "claude-haiku-4-5-20251001" },
  { label: "GPT-5.4 (OpenAI)", provider: "openai", model: "gpt-5.4" },
  { label: "GPT-5.4 mini", provider: "openai", model: "gpt-5.4-mini" },
  { label: "Gemini 3.1 Pro", provider: "gemini", model: "gemini-3.1-pro-preview" },
  { label: "Gemini 3 Flash", provider: "gemini", model: "gemini-3-flash-preview" },
];

export default function AiTranslationSettingsAdmin() {
  const { settings, set, save, saving, saved } = useSettings();
  if (!settings) return <Loading />;

  const currentPresetIdx = PRESETS.findIndex(
    (p) => p.provider === settings.ai_translate_provider && p.model === settings.ai_translate_model
  );

  const applyPreset = (idx) => {
    const p = PRESETS[idx];
    if (!p) return;
    set("ai_translate_provider", p.provider);
    set("ai_translate_model", p.model);
  };

  const provider = settings.ai_translate_provider || "anthropic";
  const providerKeyField = {
    anthropic: "ai_api_key_anthropic",
    openai: "ai_api_key_openai",
    gemini: "ai_api_key_gemini",
  }[provider];
  const hasByok = !!(settings[providerKeyField] || "").trim();

  const PROVIDER_HELP = {
    anthropic: { name: "Anthropic", url: "https://console.anthropic.com/settings/keys", prefix: "sk-ant-" },
    openai:    { name: "OpenAI",    url: "https://platform.openai.com/api-keys",       prefix: "sk-" },
    gemini:    { name: "Google AI Studio", url: "https://aistudio.google.com/apikey",  prefix: "AI" },
  }[provider];

  return (
    <div data-testid="cms-settings-ai">
      <SettingsHeader
        title="Traducción con IA"
        description="Configura el proveedor y modelo usados por el botón «Traducir con IA» del editor de contenidos."
        onSave={save}
        saving={saving}
        saved={saved}
      />

      <div className="space-y-6">
        <Section title="Estado">
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.ai_translate_enabled}
              onChange={(e) => set("ai_translate_enabled", e.target.checked)}
              className="accent-[#C8102E] mt-0.5"
              data-testid="settings-ai-enabled"
            />
            <span>
              <strong>Activar traducción asistida por IA</strong>
              <br />
              <span className="text-xs text-gray-500">
                Cuando está activa, los editores verán un botón <em>Traducir con IA</em> junto a la pestaña de idioma en los formularios de Actividades, Noticias y Páginas.
              </span>
            </span>
          </label>
        </Section>

        <Section title="Modelo">
          <p className="text-xs text-gray-500 mb-3">
            <Sparkles className="inline w-3.5 h-3.5 text-[#C8102E] mr-1" />
            Elija un preset o defina un modelo personalizado. El coste se descuenta de su saldo de la Universal Key (Emergent LLM).
          </p>
          <div className="space-y-2 mb-4">
            {PRESETS.map((p, i) => (
              <label
                key={`${p.provider}-${p.model}`}
                className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${
                  i === currentPresetIdx ? "border-[#C8102E] bg-[#FCEAED]" : "border-gray-200 hover:border-gray-400"
                }`}
                data-testid={`settings-ai-preset-${i}`}
              >
                <input
                  type="radio"
                  name="ai-preset"
                  checked={i === currentPresetIdx}
                  onChange={() => applyPreset(i)}
                  className="accent-[#C8102E]"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0F2040]">{p.label}</div>
                  <div className="text-xs text-gray-500 font-mono">{p.provider} · {p.model}</div>
                </div>
              </label>
            ))}
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-[#C8102E] hover:underline mb-2">Configuración avanzada</summary>
            <div className="grid md:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Proveedor</label>
                <select
                  value={settings.ai_translate_provider || "anthropic"}
                  onChange={(e) => set("ai_translate_provider", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 outline-none text-sm bg-white"
                  data-testid="settings-ai-provider"
                >
                  <option value="anthropic">anthropic</option>
                  <option value="openai">openai</option>
                  <option value="gemini">gemini</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">ID del modelo</label>
                <input
                  value={settings.ai_translate_model || ""}
                  onChange={(e) => set("ai_translate_model", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm font-mono"
                  data-testid="settings-ai-model"
                  placeholder="claude-sonnet-4-6"
                />
              </div>
            </div>
          </details>
        </Section>

        <Section title="Usar tu propia API key (Bring-Your-Own-Key)">
          <p className="text-xs text-gray-500 mb-4">
            <KeyRound className="inline w-3.5 h-3.5 text-[#C8102E] mr-1" />
            Si pega una clave del proveedor seleccionado, las traducciones se cobrarán en <strong>su cuenta</strong> del
            proveedor y <strong>no</strong> consumirán saldo de la Universal Key de Emergent. Si el campo está vacío
            se usa automáticamente la Universal Key. Las claves se guardan cifradas en la base de datos y solo se
            envían al proveedor seleccionado.
          </p>

          <div className="mb-4 bg-[#F8F9FB] border border-gray-200 p-3 text-xs">
            <p className="mb-1">
              Proveedor actual: <strong className="text-[#0F2040]">{PROVIDER_HELP?.name || provider}</strong>
              {hasByok ? (
                <span className="ml-2 inline-flex items-center gap-1 text-green-700 font-medium">● BYOK activo</span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 text-gray-500">○ usando Universal Key</span>
              )}
            </p>
            {PROVIDER_HELP && (
              <p className="text-gray-500">
                Obtenga su clave en{" "}
                <a href={PROVIDER_HELP.url} target="_blank" rel="noreferrer" className="text-[#C8102E] hover:underline">
                  {PROVIDER_HELP.url.replace("https://", "")}
                </a>
                {PROVIDER_HELP.prefix && (
                  <> · debe empezar por <code className="font-mono">{PROVIDER_HELP.prefix}…</code></>
                )}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {[
              { k: "ai_api_key_anthropic", label: "Anthropic (Claude)", placeholder: "sk-ant-…" },
              { k: "ai_api_key_openai",    label: "OpenAI (GPT)",       placeholder: "sk-…" },
              { k: "ai_api_key_gemini",    label: "Google Gemini",      placeholder: "AI…" },
            ].map((row) => (
              <div key={row.k}>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">
                  {row.label}
                  {settings[row.k] ? <span className="ml-2 text-[10px] text-green-700">● guardada</span> : null}
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    autoComplete="off"
                    value={settings[row.k] ?? ""}
                    onChange={(e) => set(row.k, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm font-mono"
                    placeholder={row.placeholder}
                    data-testid={`settings-${row.k.replace(/_/g, "-")}`}
                  />
                  {settings[row.k] && (
                    <button
                      type="button"
                      onClick={() => set(row.k, "")}
                      className="text-xs px-3 border border-gray-200 hover:bg-gray-50"
                      data-testid={`settings-${row.k.replace(/_/g, "-")}-clear`}
                    >
                      Borrar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
